/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEvent, useCallback, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import Tooltip from '../../tooltip/Tooltip';
import {isElementTruncated, useIsTruncated} from './useIsTruncated';

interface ClampedTextWithTooltipProps {
  children: string;
  className?: string;
  lines?: number;
}

// the line count is set inline - a prop-driven style value made JSS add a
// dynamic rule for every mounted instance (each card renders several) ...
const useStyles = makeStyles(() => ({
  clamped : {
    display         : '-webkit-box',
    overflow        : 'hidden',
    textOverflow    : 'ellipsis',
    WebkitBoxOrient : 'vertical'
  },
  // the full text in the tooltip wraps - also inside a long word - and the
  // tooltip is wider than its default: in the tooltip's own flex box an
  // unbreakable run of text could not shrink, and ran out past its edge ...
  tooltipPopper : {
    '& .MuiTooltip-tooltip' : {
      maxWidth : 360
    }
  },
  tooltipText : {
    display      : 'block',
    minWidth     : 0,
    overflowWrap : 'anywhere',
    textAlign    : 'left',
    whiteSpace   : 'normal'
  }
}));

/**
 * ClampedTextWithTooltip clamps its text content to `lines` lines (2 by
 * default), and shows a tooltip with the full text only when it actually
 * truncates - never on text short enough to fit. `Tooltip` disables itself
 * on mobile internally (`useIsPhone`), so no separate mobile handling is
 * needed here.
 *
 * ```typescript
 *
 *   import {ClampedTextWithTooltip} from
 *     '@ion/dls/src/components/assetcard/current/ClampedTextWithTooltip';
 *   ...
 *
 *   <ClampedTextWithTooltip>{assetName}</ClampedTextWithTooltip>
 *   <ClampedTextWithTooltip lines={1}>{groupName}</ClampedTextWithTooltip>
 *
 * ```
 *
 */
export const ClampedTextWithTooltip = ({
  children,
  className,
  lines = 2
}: ClampedTextWithTooltipProps) => {
  const classes     = useStyles();
  const clampStyle  = useMemo(() => ({WebkitLineClamp: lines}), [lines]);
  const elementRef  = useRef<HTMLSpanElement>(null);
  const isTruncated = useIsTruncated(elementRef, [children]);
  // measured again as the pointer arrives - a measurement taken while the
  // text was still being laid out (e.g. inside an opening accordion) could
  // miss the cut-off, and its full text never showed on hover ...
  const [isTruncatedOnHover, setTruncatedOnHover] = useState(false);
  const onMouseEnter = useCallback(function(event: MouseEvent<HTMLSpanElement>) {
    const element = event.currentTarget;
    setTruncatedOnHover(isElementTruncated(element));
  }, []);

  return (
    <Tooltip title={<span className={classes.tooltipText}>{children}</span>}
      popperClassName={classes.tooltipPopper}
      renderOnlyChildren={!isTruncated && !isTruncatedOnHover}>
      <span ref={elementRef}
        className={`${classes.clamped} ${className || ''}`}
        style={clampStyle}
        onMouseEnter={onMouseEnter}>
        {children}
      </span>
    </Tooltip>
  );
};

export default ClampedTextWithTooltip;
